import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Download, QrCode, Loader2, AlertCircle, RotateCcw, AlertTriangle, Scale, BookOpen, ExternalLink } from 'lucide-react';
import { generateVerificationQR } from '@/utils/qr';
import { generateStudyPlanPDF, downloadPDF } from '@/utils/pdf';
import { formatDate } from '@/utils/date';
import { getUser, getAuthToken, authenticatedFetch } from '@/lib/auth';
import type { StudyPlan } from '@/types';
import { useToast } from './ui';

export default function PublicVerification() {
  const { approvalLink } = useParams<{ approvalLink: string }>();
  const toast = useToast();
  const [plan, setPlan] = useState<StudyPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [reactivateReason, setReactivateReason] = useState('');
  const [reactivating, setReactivating] = useState(false);
  const [legalText, setLegalText] = useState<string | null>(null);
  const [literature, setLiterature] = useState<any[]>([]);

  const currentUser = getUser();
  const isSystemAdmin = currentUser?.role === 'systemadmin';

  useEffect(() => {
    if (approvalLink) {
      // Scroll to top when approval link changes
      window.scrollTo(0, 0);
      loadStudyPlan();
      setQrCodeUrl(generateVerificationQR(approvalLink, { size: 300 }));
    }
  }, [approvalLink]);

  const loadStudyPlan = async () => {
    try {
      // Use backend API endpoint via new proxy route (no auth required)
      // Use absolute URL to avoid issues with relative paths in deep routes
      const url = `${window.location.origin}/verify-public/${approvalLink}`;
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 404) {
          setError('Studieplan ikke funnet eller ikke godkjent');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Kunne ikke hente studieplan');
      }

      // Transform backend response to match expected format
      const studyPlan = result.study_plan;
      const transformedData: any = {
        ...studyPlan,
        study_association: {
          name: studyPlan.study_association_name,
          organization_number: studyPlan.organization_number
        },
        approver: {
          full_name: studyPlan.approver_name
        }
      };
      setPlan(transformedData);
      setLegalText(result.legal_text);

      // Load literature (no auth needed for approved plans)
      try {
        const litResponse = await fetch(`/api/study-plans/${studyPlan.id}/literature`);
        if (litResponse.ok) {
          const litData = await litResponse.json();
          if (litData.success) {
            setLiterature(litData.literature);
          }
        }
      } catch (litErr) {
        console.error('Load literature error:', litErr);
        // Non-critical, don't show error to user
      }
    } catch (err: any) {
      console.error('Load study plan error:', err);
      setError(err.message || 'En feil oppstod');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!plan) return;

    setGeneratingPDF(true);
    try {
      const pdfBlob = await generateStudyPlanPDF(plan, legalText);
      const filename = `${plan.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      downloadPDF(pdfBlob, filename);
    } catch (err: any) {
      console.error('PDF generation error:', err);
      toast.showError('Feil ved generering av PDF: ' + err.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleReactivate = async () => {
    if (!plan || !currentUser) return;

    setReactivating(true);
    setError('');

    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error('Du må være logget inn for å reaktivere');
      }

      const response = await authenticatedFetch('/api/study-plans/reactivate-version', {
        method: 'POST',
        body: JSON.stringify({
          admin_id: currentUser.id,
          version_id: (plan as any).version_id,
          reason: reactivateReason.trim() || 'Reaktivert av systemadmin',
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Kunne ikke reaktivere versjon');
      }

      // Reload the plan to show updated status
      await loadStudyPlan();
      setShowReactivateModal(false);
      setReactivateReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke reaktivere versjon');
    } finally {
      setReactivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin" size={48} />
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-600" size={64} />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Ikke funnet
            </h1>
            <p className="text-gray-600 mb-6">
              {error || 'Denne studieplanen finnes ikke eller er ikke godkjent.'}
            </p>
            <a href="/" className="btn btn-primary">
              Til forsiden
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background watermark - positioned within this container only, not covering menu */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: 'url(/background.png)',
          backgroundSize: '60%',
          backgroundPosition: 'top left',
          backgroundRepeat: 'repeat',
          opacity: 0.08
        }}
      />
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="bg-kf-primary text-white rounded-t-lg px-4 sm:px-8 py-6 relative">
          <div className="flex items-center justify-between">
            <div className="pr-20 sm:pr-24">
              <h1 className="text-2xl sm:text-3xl font-bold">Kompetanseattest</h1>
              <p className="mt-2 text-kf-light text-sm sm:text-base">Godkjent studieplan</p>
            </div>
            {/* Verification stamp */}
            <div className="absolute top-2 right-2 sm:right-4">
              <img
                src="/verifisert.png"
                alt="Verifisert"
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white shadow-lg rounded-b-lg px-4 sm:px-8 py-6">
          {/* Deactivated Warning */}
          {(plan as any).deleted_at && (
            <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertTriangle className="text-red-600 mr-3" size={24} />
                  <div>
                    <p className="font-semibold text-red-900">Deaktivert versjon</p>
                    <p className="text-sm text-red-700">
                      Denne versjonen av studieplanen er deaktivert og er ikke lenger gyldig for verifisering
                    </p>
                  </div>
                </div>
                {isSystemAdmin && (
                  <button
                    onClick={() => setShowReactivateModal(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <RotateCcw size={16} />
                    Reaktiver
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Verification Badge */}
          {!(plan as any).deleted_at && (
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <CheckCircle className="text-green-600 mr-3" size={24} />
                <div>
                  <p className="font-semibold text-green-900">Verifisert og godkjent</p>
                  <p className="text-sm text-green-700">
                    Denne studieplanen er offisielt godkjent av {plan.approver?.full_name || 'administrator'} på fullmakt fra {plan.study_association?.name || 'studieforbundet'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Study Plan Details */}
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{plan.title}</h2>
              <p className="text-gray-600">{plan.description}</p>
            </div>

            {/* Meta Information */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm text-gray-600">Kompetansepoeng</p>
                <p className="text-xl font-bold text-kf-primary">{plan.ects_credits}</p>
              </div>
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm text-gray-600">Normativ studiebelasting</p>
                <p className="text-xl font-bold text-kf-primary">{plan.estimated_hours} timer</p>
              </div>
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm text-gray-600">Valideringsscore</p>
                <p className="text-xl font-bold text-green-600">
                  {Number(plan.validation_score || 0).toFixed(1)}%
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm text-gray-600">EQF-nivå</p>
                <p className="text-xl font-bold text-kf-primary">
                  Nivå {plan.metadata.eqf_level || 'N/A'}
                </p>
              </div>
            </div>

            {/* Learning Outcomes */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Læringsutbytte</h3>

              {plan.learning_outcomes.knowledge && plan.learning_outcomes.knowledge.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Kunnskaper</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {plan.learning_outcomes.knowledge.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {plan.learning_outcomes.skills && plan.learning_outcomes.skills.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Ferdigheter</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {plan.learning_outcomes.skills.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {plan.learning_outcomes.competence && plan.learning_outcomes.competence.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Kompetanse</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {plan.learning_outcomes.competence.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Content Modules */}
            {plan.content_modules && plan.content_modules.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-4">Innholdsmoduler</h3>
                <ul className="space-y-3 text-gray-700">
                  {plan.content_modules.map((module: any, index: number) => {
                    // v6.4+: content_modules er objekter {title, description, estimated_hours, learning_outcomes}.
                    // Bakoverkompatibelt: hvis strenger (gammel datamodell), vis som før.
                    if (typeof module === 'string') {
                      return <li key={index} className="list-disc list-inside">{module}</li>;
                    }
                    const title = module?.title || `Modul ${index + 1}`;
                    const description = module?.description;
                    const hours = module?.estimated_hours;
                    return (
                      <li key={index} className="pl-4 border-l-4 border-kf-primary/30 py-1">
                        <div className="font-semibold text-gray-900">{title}</div>
                        {description && (
                          <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{description}</p>
                        )}
                        {typeof hours === 'number' && hours > 0 && (
                          <p className="text-xs text-gray-500 mt-1">Estimert tid: {hours} timer</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Assessment Criteria */}
            <div>
              <h3 className="text-xl font-semibold mb-4">Vurderingskriterier</h3>
              <p className="text-gray-700 whitespace-pre-wrap">{plan.assessment_criteria}</p>
            </div>

            {/* Literature */}
            {literature && literature.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-kf-primary" />
                  Litteraturliste
                </h3>
                <div className="space-y-3">
                  {literature.map((lit) => (
                    <div key={lit.id} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{lit.title}</h4>
                          {lit.description && (
                            <p className="text-sm text-gray-600 mt-1">{lit.description}</p>
                          )}
                          {lit.ai_summary && (
                            <p className="text-sm text-gray-700 mt-2 italic">{lit.ai_summary}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                            <span className="capitalize">{lit.literature_type}</span>
                            {lit.download_status === 'downloaded' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                Verifisert og lagret
                              </span>
                            )}
                          </div>
                        </div>
                        {lit.original_url && (
                          <a
                            href={lit.original_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-4 p-2 text-kf-primary hover:bg-kf-primary hover:text-white rounded-lg transition flex items-center gap-1 text-sm"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Åpne kilde
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Organization Info */}
            {plan.study_association && (
              <div className="border-t pt-6">
                <h3 className="text-xl font-semibold mb-4">Studieforbund</h3>
                <p className="text-gray-700">{plan.study_association.name}</p>
                {plan.study_association.organization_number && (
                  <p className="text-sm text-gray-600">
                    Org.nr: {plan.study_association.organization_number}
                  </p>
                )}
              </div>
            )}

            {/* Approval Info */}
            <div className="border-t pt-6">
              <h3 className="text-xl font-semibold mb-4">Godkjenningsinformasjon</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {plan.approved_at && (
                  <div>
                    <p className="text-gray-600">Godkjent dato</p>
                    <p className="font-medium">{formatDate(plan.approved_at)}</p>
                  </div>
                )}
                {plan.approver && (
                  <div>
                    <p className="text-gray-600">Godkjent av</p>
                    <p className="font-medium">
                      {plan.approver.full_name} etter fullmakt fra {plan.study_association?.name || 'studieforbundet'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* QR Code and Actions */}
            <div className="border-t pt-6">
              <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
                <div className="text-center">
                  <p className="font-medium mb-4">Skann for å verifisere</p>
                  {qrCodeUrl && (
                    <img
                      src={qrCodeUrl}
                      alt="QR code for verification"
                      className="mx-auto border-4 border-gray-200 rounded max-w-full h-auto"
                    />
                  )}
                  <p className="text-xs text-gray-600 mt-2 break-all">
                    {window.location.href}
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleDownloadPDF}
                    disabled={generatingPDF}
                    className="btn btn-primary flex items-center gap-2 justify-center"
                  >
                    {generatingPDF ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Genererer...
                      </>
                    ) : (
                      <>
                        <Download size={18} />
                        Last ned PDF
                      </>
                    )}
                  </button>

                  <a
                    href={qrCodeUrl}
                    download={`qr_${plan.title}.png`}
                    className="btn btn-secondary flex items-center gap-2 justify-center"
                  >
                    <QrCode size={18} />
                    Last ned QR-kode
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>© {new Date().getFullYear()} Kompetanseforbundet</p>
          <p className="mt-1">
            Denne studieplanen er godkjent i henhold til EU Micro-credentials Framework og ELMv3
          </p>
        </div>

        {/* Legal Framework */}
        {legalText && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-6">
            <div className="flex items-center gap-2 mb-3">
              <Scale className="text-kf-primary" size={20} />
              <h3 className="font-semibold text-gray-900">Lovhjemmelsgrunnlag</h3>
            </div>
            <div className="text-sm text-gray-700 whitespace-pre-wrap prose prose-sm max-w-none">
              {legalText}
            </div>
          </div>
        )}
      </div>

      {/* Reactivate Modal */}
      {showReactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-2 mb-4">
              <RotateCcw className="text-green-600" size={24} />
              <h3 className="text-xl font-bold">Reaktiver versjon</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Er du sikker på at du vil reaktivere denne versjonen? Versjonen vil igjen være tilgjengelig via den offentlige verifiseringslenken.
            </p>

            <div className="mb-4">
              <label className="label text-sm font-medium mb-2">
                Årsak til reaktivering (påkrevd):
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-kf-primary"
                rows={3}
                placeholder="F.eks: Deaktivert ved en feil, ny informasjon tilgjengelig..."
                value={reactivateReason}
                onChange={(e) => setReactivateReason(e.target.value)}
                disabled={reactivating}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleReactivate}
                disabled={reactivating || !reactivateReason.trim()}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reactivating ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Reaktiverer...
                  </>
                ) : (
                  <>
                    <RotateCcw size={18} />
                    Reaktiver
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowReactivateModal(false);
                  setReactivateReason('');
                  setError('');
                }}
                disabled={reactivating}
                className="btn btn-secondary flex-1"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
